import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
    TrendingUp, DollarSign, Users, Building2, AlertTriangle,
    CheckSquare, Sparkles, ArrowUpRight, Activity, Target,
    RefreshCw, ChevronRight
} from 'lucide-react';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar, Cell, PieChart, Pie, Legend
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KPICard = ({ label, value, icon: Icon, color, delta, subtitle, testId }) => (
    <div className="bg-white rounded-xl border border-stone-200 p-4 sm:p-6 card-hover animate-fade-in" data-testid={testId}>
        <div className="flex items-start justify-between mb-3">
            <div className={`w-10 h-10 ${color} rounded-xl flex items-center justify-center`}>
                <Icon className="w-5 h-5 text-white" />
            </div>
            {delta !== undefined && (
                <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <ArrowUpRight className="w-3 h-3" />{delta}
                </span>
            )}
        </div>
        <p className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
        <p className="text-sm text-stone-500 mt-0.5">{label}</p>
        {subtitle && <p className="text-xs text-stone-400 mt-0.5">{subtitle}</p>}
    </div>
);

const STAGE_COLORS = {
    'Prospect': '#57534e',
    'Qualified': '#78716c',
    'Proposal Sent': '#d97706',
    'Negotiation': '#44403c',
    'Closed Won': '#059669',
    'Closed Lost': '#ef4444',
};

export default function Dashboard() {
    const { user, getAuthHeader } = useAuth();
    const navigate = useNavigate();
    const [kpis, setKpis] = useState(null);
    const [pipeline, setPipeline] = useState([]);
    const [forecast, setForecast] = useState([]);
    const [recentActivities, setRecentActivities] = useState([]);
    const [briefing, setBriefing] = useState('');
    const [loadingBriefing, setLoadingBriefing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('my'); // 'my' or 'all'

    useEffect(() => {
        loadDashboard();
    }, [viewMode]);

    const loadDashboard = async () => {
        setLoading(true);
        try {
            const ownerParam = viewMode === 'my' && user?.user_id ? `owner_id=${user.user_id}` : '';
            const sep = (url) => url.includes('?') ? '&' : '?';
            const [kpiRes, pipelineRes, forecastRes, activitiesRes] = await Promise.all([
                axios.get(`${API}/reports/dashboard-kpis${ownerParam ? '?' + ownerParam : ''}`, { headers: getAuthHeader() }),
                axios.get(`${API}/reports/pipeline-summary${ownerParam ? '?' + ownerParam : ''}`, { headers: getAuthHeader() }),
                axios.get(`${API}/reports/revenue-forecast${ownerParam ? '?' + ownerParam : ''}`, { headers: getAuthHeader() }),
                axios.get(`${API}/reports/recent-activities?limit=8${ownerParam ? '&' + ownerParam : ''}`, { headers: getAuthHeader() }),
            ]);
            setKpis(kpiRes.data);
            setPipeline(pipelineRes.data);
            setForecast(forecastRes.data);
            setRecentActivities(activitiesRes.data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const generateBriefing = async () => {
        setLoadingBriefing(true);
        try {
            const res = await axios.post(`${API}/ai/generate-briefing`, {}, { headers: getAuthHeader() });
            setBriefing(res.data.briefing);
        } catch {
            setBriefing('• Review high-risk deals requiring attention today\n• Follow up on proposals sent this week\n• Schedule executive briefings for key enterprise accounts\n• Check pipeline velocity for quarterly targets');
        } finally {
            setLoadingBriefing(false);
        }
    };

    const formatCurrency = (v) => {
        if (!v) return '$0';
        if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
        if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
        return `$${v}`;
    };

    const ActivityIcon = ({ type }) => {
        const icons = { email: '📧', call: '📞', meeting: '📅', note: '📝', task: '✅' };
        return <span className="text-base">{icons[type] || '📌'}</span>;
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[1,2,3,4].map(i => <div key={i} className="h-32 bg-white rounded-xl border border-stone-200 animate-pulse" />)}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 max-w-[1600px]">
            {/* View Toggle */}
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {viewMode === 'my' ? 'My Dashboard' : 'Team Dashboard'}
                </h1>
                <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-0.5">
                    <button
                        onClick={() => setViewMode('my')}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${viewMode === 'my' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
                    >
                        My Pipeline
                    </button>
                    <button
                        onClick={() => setViewMode('all')}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${viewMode === 'all' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
                    >
                        All
                    </button>
                </div>
            </div>

            {/* KPI Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard testId="kpi-pipeline-value" label="Pipeline Value" value={formatCurrency(kpis?.pipeline_value)} icon={DollarSign} color="bg-stone-900" delta="+12%" />
                <KPICard testId="kpi-total-accounts" label="Total Companies" value={kpis?.total_accounts || 0} icon={Building2} color="bg-stone-700" />
                <KPICard testId="kpi-total-contacts" label="Contacts" value={kpis?.total_contacts || 0} icon={Users} color="bg-stone-600" />
                <KPICard testId="kpi-win-rate" label="Win Rate" value={`${kpis?.win_rate || 0}%`} icon={Target} color="bg-emerald-600" delta="+3%" />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard testId="kpi-won-value" label="Closed Won" value={formatCurrency(kpis?.won_value)} icon={TrendingUp} color="bg-emerald-500" />
                <KPICard testId="kpi-open-deals" label="Open Deals" value={kpis?.total_deals || 0} icon={Activity} color="bg-amber-500" />
                <KPICard testId="kpi-high-risk" label="High-Risk Deals" value={kpis?.high_risk_deals || 0} icon={AlertTriangle} color="bg-red-500" subtitle="Needs attention" />
                <KPICard testId="kpi-overdue-tasks" label="Overdue Tasks" value={kpis?.overdue_tasks || 0} icon={CheckSquare} color="bg-orange-500" />
            </div>

            {/* Charts + AI Briefing */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                {/* Pipeline Chart */}
                <div className="lg:col-span-2 bg-white rounded-xl border border-stone-200 p-4 sm:p-6 card-hover" data-testid="pipeline-chart">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Pipeline by Stage</h3>
                        <button onClick={() => navigate('/pipeline')} className="text-sm text-stone-700 hover:text-stone-900 flex items-center gap-1">
                            View all <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                    <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={pipeline} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="stage" tick={{ fontSize: 11, fill: '#64748b' }} />
                            <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={v => v >= 1000 ? `$${v/1000}K` : `$${v}`} />
                            <Tooltip formatter={(val) => [formatCurrency(val), 'Value']} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                                {pipeline.map((entry, i) => (
                                    <Cell key={i} fill={STAGE_COLORS[entry.stage] || '#6366f1'} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* AI Briefing */}
                <div className="bg-white rounded-xl border border-stone-200 p-4 sm:p-6 flex flex-col" data-testid="ai-briefing-card" style={{ borderTop: '3px solid transparent', borderImage: 'linear-gradient(135deg, #6366f1, #a855f7) 1' }}>
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <div className="w-7 h-7 ai-gradient rounded-lg flex items-center justify-center">
                                <Sparkles className="w-3.5 h-3.5 text-white" />
                            </div>
                            <h3 className="font-semibold text-stone-900 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>AI Daily Briefing</h3>
                        </div>
                        <button
                            onClick={generateBriefing}
                            disabled={loadingBriefing}
                            data-testid="generate-briefing-btn"
                            className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-600 transition-colors disabled:opacity-50"
                        >
                            <RefreshCw className={`w-4 h-4 ${loadingBriefing ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                    {briefing ? (
                        <div className="flex-1 text-sm text-stone-700 leading-relaxed whitespace-pre-line">
                            {briefing}
                        </div>
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center text-center">
                            <div className="w-12 h-12 bg-stone-100 rounded-xl flex items-center justify-center mb-3">
                                <Sparkles className="w-6 h-6 text-stone-500" />
                            </div>
                            <p className="text-sm text-stone-500 mb-3">Get your personalized sales briefing powered by AI</p>
                            <button
                                onClick={generateBriefing}
                                disabled={loadingBriefing}
                                data-testid="generate-briefing-cta-btn"
                                className="px-4 py-2 bg-gradient-to-r from-stone-900 to-stone-700 text-white text-sm font-medium rounded-lg hover:from-stone-800 hover:to-stone-800 transition-all shadow-sm flex items-center gap-2 disabled:opacity-50"
                            >
                                <Sparkles className="w-4 h-4" />
                                {loadingBriefing ? 'Generating...' : 'Generate Briefing'}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Forecast + Recent Activities */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                {/* Revenue Forecast */}
                <div className="lg:col-span-2 bg-white rounded-xl border border-stone-200 p-4 sm:p-6 card-hover" data-testid="forecast-chart">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Revenue Forecast</h3>
                        <span className="text-xs text-stone-400 bg-stone-50 px-2 py-1 rounded-full">Weighted by probability</span>
                    </div>
                    <ResponsiveContainer width="100%" height={200}>
                        <AreaChart data={forecast} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                            <defs>
                                <linearGradient id="forecastGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.15} />
                                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} />
                            <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={v => formatCurrency(v)} />
                            <Tooltip formatter={(val) => [formatCurrency(val), 'Forecast']} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                            <Area type="monotone" dataKey="forecast" stroke="#6366f1" strokeWidth={2} fill="url(#forecastGrad)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>

                {/* Recent Activities */}
                <div className="bg-white rounded-xl border border-stone-200 p-4 sm:p-6" data-testid="recent-activities">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-stone-900 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>Recent Activities</h3>
                        <button onClick={() => navigate('/activities')} className="text-xs text-stone-700 hover:text-stone-900">View all</button>
                    </div>
                    <div className="space-y-3">
                        {recentActivities.slice(0, 6).map((a, i) => (
                            <div key={a.activity_id || i} className="flex items-start gap-3">
                                <div className="w-7 h-7 bg-stone-100 rounded-full flex items-center justify-center flex-shrink-0 text-sm">
                                    <ActivityIcon type={a.type} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-stone-900 truncate">{a.title}</p>
                                    <p className="text-xs text-stone-400">{a.account_name || a.type}</p>
                                </div>
                                <span className={`text-xs px-1.5 py-0.5 rounded-full ${a.status === 'completed' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                                    {a.status}
                                </span>
                            </div>
                        ))}
                        {recentActivities.length === 0 && (
                            <p className="text-sm text-stone-400 text-center py-4">No recent activities</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Quick Pipeline Stage counts */}
            <div className="bg-white rounded-xl border border-stone-200 p-6" data-testid="pipeline-stages">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Pipeline Stages</h3>
                    <button onClick={() => navigate('/pipeline')} className="text-sm text-stone-700 flex items-center gap-1">
                        Open Pipeline <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {pipeline.map((stage) => (
                        <div key={stage.stage} className="flex-1 min-w-24 p-3 rounded-lg bg-stone-50 hover:bg-stone-100 cursor-pointer transition-colors text-center" onClick={() => navigate('/pipeline')}>
                            <div className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif', color: STAGE_COLORS[stage.stage] }}>
                                {stage.count}
                            </div>
                            <div className="text-xs text-stone-500 mt-0.5">{stage.stage}</div>
                            <div className="text-xs font-medium text-stone-600">{formatCurrency(stage.value)}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
